'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { Update } from '../lib/updates'

interface NewsTickerProps {
  updates: Update[]
}

/**
 * Auto-scrolling news ticker banner
 * Features:
 * - Auto-scroll horizontally (leftward loop)
 * - Pause on hover and focus
 * - Respects prefers-reduced-motion
 * - Swipeable/scrollable on mobile
 * - Accessible with keyboard navigation
 */
export function NewsTicker({ updates }: NewsTickerProps) {
  const [isPaused, setIsPaused] = useState(false)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const animationRef = useRef<number>()

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mediaQuery.matches)

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches)
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  // Auto-scroll animation
  useEffect(() => {
    if (prefersReducedMotion || isPaused || !scrollRef.current) return

    const container = scrollRef.current
    let scrollPosition = 0
    const scrollSpeed = 0.5 // pixels per frame

    const animate = () => {
      scrollPosition += scrollSpeed

      // Reset when we've scrolled past half (since we duplicate content)
      if (scrollPosition >= container.scrollWidth / 2) {
        scrollPosition = 0
      }

      container.scrollLeft = scrollPosition
      animationRef.current = requestAnimationFrame(animate)
    }

    animationRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [prefersReducedMotion, isPaused])

  if (updates.length === 0) return null

  return (
    <div className="border-b border-storm/10 bg-white">
      <div
        className="relative mx-auto flex max-w-7xl items-center gap-4 overflow-hidden px-4 py-2"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onFocus={() => setIsPaused(true)}
        onBlur={() => setIsPaused(false)}
      >
        {/* Label */}
        <div className="flex-shrink-0 text-xs font-semibold uppercase tracking-wider text-storm">
          Updates
        </div>

        {/* Scrolling container */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-x-auto scrollbar-hide"
          style={{ scrollBehavior: prefersReducedMotion ? 'auto' : 'smooth' }}
        >
          <div className="flex gap-6">
            {/* Render items twice for seamless loop */}
            {[...updates, ...updates].map((update, index) => (
              <TickerItem key={`${update.id}-${index}`} update={update} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function TickerItem({ update }: { update: Update }) {
  // Use tickerText if available, otherwise fall back to title
  const displayText = update.tickerText || update.title

  const content = (
    <div className="flex flex-shrink-0 items-center gap-2 whitespace-nowrap">
      <UpdateIcon type={update.type} />
      <span className="text-sm text-navy">{displayText}</span>
    </div>
  )

  if (update.href) {
    return (
      <Link href={update.href} className="transition-opacity hover:opacity-70">
        {content}
      </Link>
    )
  }

  return <div>{content}</div>
}

function UpdateIcon({ type }: { type: Update['type'] }) {
  const iconClasses = 'h-4 w-4 flex-shrink-0'

  switch (type) {
    case 'info':
      return (
        <svg
          className={`${iconClasses} text-navy`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      )
    case 'sponsor':
      return (
        <svg
          className={`${iconClasses} text-ice`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      )
    case 'result':
      return (
        <svg
          className={`${iconClasses} text-navy`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
          />
        </svg>
      )
    case 'alert':
      return (
        <svg
          className={`${iconClasses} text-storm`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      )
  }
}
